import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Container from "@/components/base/Container";
import PageHeader from "@/components/feature/PageHeader";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Reveal from "@/components/feature/Reveal";
import { articles, topics } from "@/mocks/melons";
import { getTopicTone, topicChipTone } from "@/utils/tone";

type FlatEvent = {
  key: string;
  date: string;
  at: string;
  title: string;
  detail: string;
  articleSlug: string;
  articleTitle: string;
  topicSlug: string;
  topicName: string;
};

export default function TimelinePage() {
  const [params, setParams] = useSearchParams();
  const activeTopic = params.get("topic") ?? "all";

  const events = useMemo<FlatEvent[]>(() => {
    const list: FlatEvent[] = [];
    articles.forEach((article) => {
      article.timeline.forEach((event, index) => {
        list.push({
          key: `${article.slug}-${index}`,
          date: article.date,
          at: event.at,
          title: event.title,
          detail: event.detail,
          articleSlug: article.slug,
          articleTitle: article.title,
          topicSlug: article.topicSlug,
          topicName: article.topicName,
        });
      });
    });
    return list
      .filter((item) => activeTopic === "all" || item.topicSlug === activeTopic)
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeTopic]);

  const updateTopic = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === "all") {
      next.delete("topic");
    } else {
      next.set("topic", value);
    }
    setParams(next, { replace: true });
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "https://shenguashe.example.com";

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "全网时间线",
    description: "按时间倒序排列的事件节点，把分散的热门大瓜片段还原成一条连续的完整时间线。",
    url: `${origin}${__BASE_PATH__}timeline`,
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: events.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: `${item.date} ${item.title}`,
        url: `${origin}${__BASE_PATH__}article/${item.articleSlug}`,
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${origin}${__BASE_PATH__}` },
      { "@type": "ListItem", position: 2, name: "全网时间线", item: `${origin}${__BASE_PATH__}timeline` },
    ],
  };

  return (
    <>
      <Seo
        title="全网时间线｜热门大瓜完整时间线合集 - 深瓜社"
        description="深瓜社全网时间线，按时间倒序汇总各起热门大瓜的关键节点，把未删减原稿与公开痕迹拼回一条连续时间线，方便你复盘完整来龙去脉。"
        keywords="完整时间线,热门大瓜,大瓜深扒,未删减版,深度解析,事件时间轴"
        path="/timeline"
        jsonLd={[collectionSchema, breadcrumbSchema]}
      />
      <SiteHeader />
      <main>
        <PageHeader
          crumbs={[{ label: "时间线" }]}
          eyebrow="全网时间线"
          title="所有节点，按发生顺序排好"
          description="把分散在各个选题里的关键节点汇总到一起。你可以按分类收窄范围，也可以顺着日期一路读下去，看看事情究竟是从哪一步开始转向的。"
        />

        <Container className="py-10 md:py-14">
          <div className="flex flex-wrap items-center gap-2 border-b border-background-200 pb-6">
            <button
              type="button"
              onClick={() => updateTopic("all")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                activeTopic === "all"
                  ? "bg-foreground-900 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              全部节点
            </button>
            {topics.map((topic) => (
              <button
                key={topic.slug}
                type="button"
                onClick={() => updateTopic(topic.slug)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  activeTopic === topic.slug
                    ? "bg-foreground-900 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {topic.name}
              </button>
            ))}
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            当前展示 <strong className="text-foreground-900">{events.length}</strong> 个节点
          </p>

          <div className="mt-8 flex flex-col">
            {events.map((item, index) => {
              const tone = getTopicTone(item.topicSlug);
              return (
                <Reveal key={item.key} delay={Math.min(index, 6) * 50}>
                  <div className="grid grid-cols-1 gap-4 border-b border-background-200 py-6 md:grid-cols-12 md:gap-6">
                    <div className="flex flex-row items-center gap-3 md:col-span-3 md:flex-col md:items-start">
                      <span className="font-heading text-lg font-bold text-foreground-950">{item.at}</span>
                      <span className="text-xs text-foreground-500">整理于 {item.date}</span>
                    </div>
                    <div className="md:col-span-9">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] ${topicChipTone[tone]}`}>
                        {item.topicName}
                      </span>
                      <h2 className="mt-3 font-heading text-lg font-semibold text-foreground-950">{item.title}</h2>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.detail}</p>
                      <Link
                        to={`/article/${item.articleSlug}`}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 transition-colors hover:text-primary-600"
                      >
                        出自 · {item.articleTitle}
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {events.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">这个分类下还没有节点</h2>
              <p className="mt-2 text-sm text-foreground-600">换个分类看看，或者回到瓜田浏览全部整理稿。</p>
              <Link
                to="/topics"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                回到瓜田
              </Link>
            </div>
          ) : null}
        </Container>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}